import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import { getApartment } from '../api/client';
import './ApartmentPage.css';

function useSwipe(onSwipeLeft, onSwipeRight) {
  const touchStart = useRef(null);
  const touchEnd = useRef(null);
  const minSwipeDistance = 50;

  const onTouchStart = useCallback((e) => {
    touchEnd.current = null;
    touchStart.current = e.targetTouches[0].clientX;
  }, []);

  const onTouchMove = useCallback((e) => {
    touchEnd.current = e.targetTouches[0].clientX;
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!touchStart.current || !touchEnd.current) return;
    const distance = touchStart.current - touchEnd.current;
    if (Math.abs(distance) >= minSwipeDistance) {
      if (distance > 0) onSwipeLeft();
      else onSwipeRight();
    }
  }, [onSwipeLeft, onSwipeRight]);

  return { onTouchStart, onTouchMove, onTouchEnd };
}

export default function ApartmentPage() {
  const { id } = useParams();
  const [apt, setApt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    getApartment(id)
      .then(setApt)
      .catch(() => setError('Квартира не найдена'))
      .finally(() => setLoading(false));
  }, [id]);

  const photos = apt?.photos || [];
  const hasPhotos = photos.length > 0;

  const nextPhoto = useCallback(() => {
    if (hasPhotos) setSelectedPhoto(p => p < photos.length - 1 ? p + 1 : 0);
  }, [hasPhotos, photos.length]);

  const prevPhoto = useCallback(() => {
    if (hasPhotos) setSelectedPhoto(p => p > 0 ? p - 1 : photos.length - 1);
  }, [hasPhotos, photos.length]);

  const gallerySwipe = useSwipe(nextPhoto, prevPhoto);
  const lightboxSwipe = useSwipe(nextPhoto, prevPhoto);

  if (loading) return <div style={{ padding: 60, textAlign: 'center' }}><div className="spinner" style={{ width: 40, height: 40 }} /></div>;
  if (error || !apt) return <div className="container" style={{ padding: 40 }}><div className="alert alert-error">{error}</div></div>;

  return (
    <div>
      <Header showBack />

      {/* Photo gallery with swipe */}
      {hasPhotos && (
        <div style={{ background: 'var(--gray-900)', position: 'relative' }}>
          <div className="container" style={{ maxWidth: 900, padding: '0' }}>
            <div
              {...gallerySwipe}
              style={{ position: 'relative', touchAction: 'pan-y' }}
            >
              <img
                src={photos[selectedPhoto]}
                alt={`${apt.name} — фото ${selectedPhoto + 1}`}
                onClick={() => setLightbox(true)}
                style={{
                  width: '100%',
                  height: 400,
                  objectFit: 'cover',
                  cursor: 'pointer',
                  display: 'block',
                  userSelect: 'none',
                }}
                draggable={false}
              />

              {/* Swipe dots */}
              {photos.length > 1 && (
                <div style={{
                  position: 'absolute',
                  bottom: 16,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  gap: 6,
                }}>
                  {photos.map((_, i) => (
                    <div
                      key={i}
                      onClick={() => setSelectedPhoto(i)}
                      style={{
                        width: i === selectedPhoto ? 20 : 8,
                        height: 8,
                        borderRadius: 4,
                        background: i === selectedPhoto ? 'white' : 'rgba(255,255,255,0.5)',
                        transition: 'all 0.2s',
                        cursor: 'pointer',
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Thumbnails — desktop */}
            {photos.length > 1 && (
              <div className="gallery-thumbs">
                {photos.map((p, i) => (
                  <img
                    key={i}
                    src={p}
                    alt={`Фото ${i + 1}`}
                    onClick={() => setSelectedPhoto(i)}
                    style={{
                      width: 72,
                      height: 52,
                      objectFit: 'cover',
                      borderRadius: 6,
                      cursor: 'pointer',
                      opacity: i === selectedPhoto ? 1 : 0.5,
                      border: i === selectedPhoto ? '2px solid white' : '2px solid transparent',
                      transition: 'opacity 0.2s, border 0.2s',
                      flexShrink: 0,
                    }}
                  />
                ))}
              </div>
            )}

            <div style={{
              position: 'absolute',
              top: 16,
              right: 16,
              background: 'rgba(0,0,0,0.6)',
              color: 'white',
              padding: '4px 12px',
              borderRadius: 6,
              fontSize: 13,
            }}>
              {selectedPhoto + 1} / {photos.length}
            </div>
          </div>
        </div>
      )}

      {/* Lightbox with swipe */}
      {lightbox && hasPhotos && (
        <div
          onClick={() => setLightbox(false)}
          {...lightboxSwipe}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.95)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'zoom-out', touchAction: 'pan-y',
          }}
        >
          <img
            src={photos[selectedPhoto]}
            alt="Увеличенное фото"
            style={{ maxWidth: '95vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 4, userSelect: 'none' }}
            onClick={e => e.stopPropagation()}
            draggable={false}
          />

          {/* Lightbox dots */}
          <div style={{
            position: 'absolute', bottom: 30, left: '50%', transform: 'translateX(-50%)',
            display: 'flex', gap: 6,
          }}>
            {photos.map((_, i) => (
              <div key={i} style={{
                width: i === selectedPhoto ? 20 : 8, height: 8,
                borderRadius: 4,
                background: i === selectedPhoto ? 'white' : 'rgba(255,255,255,0.4)',
                transition: 'all 0.2s',
              }} />
            ))}
          </div>

          {photos.length > 1 && (
            <>
              <button
                onClick={e => { e.stopPropagation(); prevPhoto(); }}
                style={lbBtn('left')}
              >‹</button>
              <button
                onClick={e => { e.stopPropagation(); nextPhoto(); }}
                style={lbBtn('right')}
              >›</button>
            </>
          )}
          <button
            onClick={() => setLightbox(false)}
            style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', fontSize: 28, cursor: 'pointer', width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >✕</button>
        </div>
      )}

      <div className="container" style={{ maxWidth: 900, padding: '24px 16px 80px' }}>
        <div className="apt-detail-layout">
          <div className="apt-detail-info">
            <h1 style={{ fontSize: 26, fontWeight: 700, marginBottom: 6 }}>{apt.name}</h1>
            <p style={{ color: 'var(--gray-500)', marginBottom: 16 }}>📍 {apt.address}</p>

            <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap', fontSize: 14, color: 'var(--gray-600)' }}>
              <span>👥 до {apt.max_guests} гостей</span>
              <span>💰 {new Intl.NumberFormat('ru-RU').format(apt.price_per_night_rub)} ₽/ночь</span>
              {apt.area_sqm && <span>📐 {apt.area_sqm} м²</span>}
              {apt.rooms && <span>🚪 {apt.rooms} комн.</span>}
              {apt.floor && <span>🏢 {apt.floor}{apt.total_floors ? `/${apt.total_floors}` : ''} эт.</span>}
            </div>

            {apt.description && (
              <p style={{ lineHeight: 1.7, color: 'var(--gray-700)', marginBottom: 24 }}>{apt.description}</p>
            )}

            {apt.amenities?.length > 0 && (
              <div>
                <h3 style={{ fontWeight: 700, marginBottom: 10, fontSize: 16 }}>Удобства</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {apt.amenities.map(a => (
                    <span key={a} style={{
                      background: 'var(--gray-100)',
                      padding: '5px 14px',
                      borderRadius: 99,
                      fontSize: 13,
                    }}>✓ {a}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="apt-detail-sidebar">
            <div className="apt-price-card card">
              <p style={{ fontSize: 24, fontWeight: 700, color: 'var(--primary)', marginBottom: 4 }}>
                {new Intl.NumberFormat('ru-RU').format(apt.price_per_night_rub)} ₽
                <span style={{ fontSize: 14, fontWeight: 400, color: 'var(--gray-500)' }}> / ночь</span>
              </p>
              <Link to={`/book/${apt.id}`} className="btn btn-primary btn-full" style={{ marginBottom: 8 }}>
                Забронировать
              </Link>
              <Link to="/chat" className="btn btn-secondary btn-full">
                💬 Задать вопрос
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="apt-mobile-bar">
        <div>
          <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--primary)' }}>
            {new Intl.NumberFormat('ru-RU').format(apt.price_per_night_rub)} ₽
          </span>
          <span style={{ fontSize: 13, color: 'var(--gray-500)' }}> / ночь</span>
        </div>
        <Link to={`/book/${apt.id}`} className="btn btn-primary" style={{ padding: '10px 24px' }}>
          Забронировать
        </Link>
      </div>
    </div>
  );
}

function lbBtn(side) {
  return {
    position: 'absolute',
    [side]: 16, top: '50%', transform: 'translateY(-50%)',
    background: 'rgba(255,255,255,0.15)', border: 'none',
    color: 'white', fontSize: 36, cursor: 'pointer',
    width: 48, height: 48, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
}